import * as React from 'react';
import {
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  Flex,
  Heading,
  IconButton,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { IScoreboardSettings as ILegacyScoreboardSettings } from '../../../data/types';
import { useEntities } from '../../../hooks/useEntities';
import { CloseButton } from '../../shared/Button';
import { Icon } from '../../shared/Icon';
import { ScoreboardErrorBoundary } from './components/ScoreboardErrorBoundary';
import { useLogoUpload } from './hooks/useLogoUpload';
import {
  toExtendedSettings,
  deriveSplitViewSegmentType,
  useSettingsHandlers,
  SegmentType,
} from './hooks/useSettingsHandlers';
import { useSettingsHydration } from './hooks/useSettingsHydration';
import { AppearanceTabContent, DataTabContent } from './settings';
import { IScoreboardSettings } from './types';

interface IProps {
  settings: ILegacyScoreboardSettings;
  disabled: boolean;
  iconOnly?: boolean;
}

const ScoreboardSettingsInternal = (props: IProps): React.ReactElement => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [currentSettings, setCurrentSettings] =
    React.useState<IScoreboardSettings | null>((): IScoreboardSettings | null =>
      toExtendedSettings(props.settings)
    );
  const [segmentType, setSegmentType] = React.useState<SegmentType>(
    (): SegmentType =>
      deriveSplitViewSegmentType(toExtendedSettings(props.settings)?.splitView)
  );
  const { groups, teams } = useEntities();

  const {
    selectedTeam,
    setSelectedTeam,
    selectedGroup,
    setSelectedGroup,
    selectedLeftColumn,
    setSelectedLeftColumn,
    selectedRightColumn,
    setSelectedRightColumn,
    selectedLeftColumnSecondaryFilter,
    setSelectedLeftColumnSecondaryFilter,
    selectedRightColumnSecondaryFilter,
    setSelectedRightColumnSecondaryFilter,
    groupOptions,
    filteredGroupOptions,
    filteredGroupOptionsForSplit,
    filteredGroupOptionsForLeftColumn,
    filteredGroupOptionsForRightColumn,
  } = useSettingsHydration({ currentSettings, groups, teams });

  React.useEffect((): void => {
    const extended = toExtendedSettings(props.settings);
    setCurrentSettings(extended);
    setSegmentType(deriveSplitViewSegmentType(extended?.splitView));
  }, [props.settings]);

  const {
    handleTeamChange,
    handleGroupChange,
    handleSegmentTypeChange,
    handleLeftColumnChange,
    handleRightColumnChange,
    handleLeftColumnTagsChange,
    handleRightColumnTagsChange,
    handleLeftColumnSecondaryFilterChange,
    handleRightColumnSecondaryFilterChange,
    handleTestTypeChange,
    handleMetricChange,
    handleDateChange,
    handleTagsChange,
    updateTheme,
    updateDisplay,
    updateBranding,
    resetAppearanceDefaults,
    applyChanges,
    handleClose,
    isSaving,
    saveError,
    clearSaveError,
  } = useSettingsHandlers({
    savedSettings: props.settings,
    setCurrentSettings,
    setSegmentType,
    setSelectedTeam,
    setSelectedGroup,
    setSelectedLeftColumn,
    setSelectedRightColumn,
    setSelectedLeftColumnSecondaryFilter,
    setSelectedRightColumnSecondaryFilter,
    setOpen,
    currentSettings,
  });

  const { uploadingLogo, uploadError, clearUploadError, handleLogoUpload } =
    useLogoUpload(updateBranding);

  return (
    <>
      {props.iconOnly ? (
        <IconButton
          onClick={(): void => setOpen(!open)}
          disabled={props.disabled}
          aria-label='Open scoreboard settings'
          w='40px'
          h='40px'
          borderRadius='lg'
          bg='rgba(255, 255, 255, 0.1)'
          backdropFilter='blur(8px)'
          color='var(--scoreboard-text-primary, #ffffff)'
          transition='all 0.2s ease-in-out'
          _hover={{ bg: 'rgba(255, 255, 255, 0.2)' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          <Icon name='settings' />
        </IconButton>
      ) : (
        <Button
          onClick={(): void => setOpen(!open)}
          disabled={props.disabled}
          variant='outline'
          size='sm'
          aria-label='Open scoreboard settings'
        >
          <Icon name='settings' />
          <Text ml={1}>Settings</Text>
        </Button>
      )}

      <DialogRoot
        open={open}
        onOpenChange={(details: { open: boolean }): void => {
          if (!details.open) {
            handleClose();
          } else {
            setOpen(true);
          }
        }}
        placement='center'
        size='md'
        role='dialog'
        aria-labelledby='scoreboard-settings-title'
      >
        <DialogBackdrop bg='blackAlpha.700' backdropFilter='blur(4px)' />
        <DialogPositioner>
          <DialogContent
            bg='var(--scoreboard-background, #1a1a2e)'
            color='var(--scoreboard-text-primary, #ffffff)'
            borderRadius='xl'
            border='1px solid rgba(255, 255, 255, 0.1)'
            boxShadow='0 8px 32px rgba(0, 0, 0, 0.4)'
            maxH='80vh'
            display='flex'
            flexDirection='column'
          >
            <DialogHeader
              borderBottom='1px solid rgba(255, 255, 255, 0.1)'
              pb={4}
            >
              <Flex align='center' justify='space-between' w='100%'>
                <Heading
                  id='scoreboard-settings-title'
                  as='h3'
                  fontSize='20px'
                  fontWeight={700}
                  color='var(--scoreboard-text-primary, #ffffff)'
                >
                  Scoreboard Settings
                </Heading>
                <DialogCloseTrigger asChild>
                  <CloseButton
                    onClick={handleClose}
                    aria-label='Close settings dialog'
                  />
                </DialogCloseTrigger>
              </Flex>
            </DialogHeader>

            {currentSettings && (
              <Tabs.Root
                defaultValue='data'
                display='flex'
                flexDirection='column'
                flex={1}
                overflow='hidden'
                aria-label='Settings tabs'
              >
                <Tabs.List
                  borderBottom='1px solid rgba(255, 255, 255, 0.1)'
                  px={6}
                  flexShrink={0}
                  role='tablist'
                >
                  <Tabs.Trigger
                    value='data'
                    color='var(--scoreboard-text-secondary, #b0b0b0)'
                    bg='transparent'
                    px={4}
                    py={3}
                    borderBottom='2px solid transparent'
                    _selected={{
                      color: 'var(--scoreboard-text-primary, #ffffff)',
                      borderBottomColor: 'var(--scoreboard-primary, #1a73e8)',
                    }}
                    fontSize='sm'
                    fontWeight={600}
                  >
                    Data
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value='appearance'
                    color='var(--scoreboard-text-secondary, #b0b0b0)'
                    bg='transparent'
                    px={4}
                    py={3}
                    borderBottom='2px solid transparent'
                    _selected={{
                      color: 'var(--scoreboard-text-primary, #ffffff)',
                      borderBottomColor: 'var(--scoreboard-primary, #1a73e8)',
                    }}
                    fontSize='sm'
                    fontWeight={600}
                  >
                    Appearance
                  </Tabs.Trigger>
                </Tabs.List>

                <DialogBody py={4} flex={1} overflowY='auto'>
                  <Tabs.Content value='data'>
                    <DataTabContent
                      settings={currentSettings}
                      segmentType={segmentType}
                      selectedTeam={selectedTeam}
                      selectedGroup={selectedGroup}
                      selectedLeftColumn={selectedLeftColumn}
                      selectedRightColumn={selectedRightColumn}
                      selectedLeftColumnSecondaryFilter={
                        selectedLeftColumnSecondaryFilter
                      }
                      selectedRightColumnSecondaryFilter={
                        selectedRightColumnSecondaryFilter
                      }
                      groupOptions={groupOptions}
                      filteredGroupOptions={filteredGroupOptions}
                      filteredGroupOptionsForSplit={
                        filteredGroupOptionsForSplit
                      }
                      filteredGroupOptionsForLeftColumn={
                        filteredGroupOptionsForLeftColumn
                      }
                      filteredGroupOptionsForRightColumn={
                        filteredGroupOptionsForRightColumn
                      }
                      onSegmentTypeChange={handleSegmentTypeChange}
                      onTeamChange={handleTeamChange}
                      onGroupChange={handleGroupChange}
                      onLeftColumnChange={handleLeftColumnChange}
                      onRightColumnChange={handleRightColumnChange}
                      onLeftColumnTagsChange={handleLeftColumnTagsChange}
                      onRightColumnTagsChange={handleRightColumnTagsChange}
                      onLeftColumnSecondaryFilterChange={
                        handleLeftColumnSecondaryFilterChange
                      }
                      onRightColumnSecondaryFilterChange={
                        handleRightColumnSecondaryFilterChange
                      }
                      onTestTypeChange={handleTestTypeChange}
                      onMetricChange={handleMetricChange}
                      onDateChange={handleDateChange}
                      onTagsChange={handleTagsChange}
                    />
                  </Tabs.Content>

                  <Tabs.Content value='appearance'>
                    <AppearanceTabContent
                      theme={currentSettings.theme}
                      display={currentSettings.display}
                      branding={currentSettings.branding}
                      uploadingLogo={uploadingLogo}
                      uploadError={uploadError}
                      onClearUploadError={clearUploadError}
                      onUpdateTheme={updateTheme}
                      onUpdateDisplay={updateDisplay}
                      onUpdateBranding={updateBranding}
                      onLogoUpload={handleLogoUpload}
                    />
                  </Tabs.Content>
                </DialogBody>
              </Tabs.Root>
            )}

            <DialogFooter borderTop='1px solid rgba(255, 255, 255, 0.1)' pt={4}>
              <Flex w='100%' justify='space-between' align='center'>
                <Button
                  onClick={resetAppearanceDefaults}
                  variant='outline'
                  size='sm'
                  color='var(--scoreboard-text-secondary, #b0b0b0)'
                  borderColor='rgba(255, 255, 255, 0.2)'
                  _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                  aria-label='Reset appearance settings to default values'
                >
                  Reset Defaults
                </Button>
                <Button
                  onClick={(): void => {
                    clearSaveError();
                    applyChanges();
                  }}
                  size='sm'
                  bg='primary'
                  color='white'
                  _hover={{ opacity: 0.9 }}
                  disabled={
                    isSaving ||
                    !Boolean(
                      currentSettings?.metrics?.[
                        currentSettings?.testType?.value
                      ]
                    )
                  }
                  aria-label='Save scoreboard settings'
                  loading={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Flex>
              {saveError && (
                <Text color='red.400' fontSize='sm' mt={2} textAlign='center'>
                  {saveError}
                </Text>
              )}
            </DialogFooter>
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </>
  );
};

/**
 * ScoreboardSettings component wrapped with error boundary for graceful error handling
 */
export const ScoreboardSettings = (props: IProps): React.ReactElement => (
  <ScoreboardErrorBoundary>
    <ScoreboardSettingsInternal {...props} />
  </ScoreboardErrorBoundary>
);
